// «Invitar» in a call — the PAGE half (shared/API.md §30.3). Web only: the Windows call host
// (src/callhost/) gets the same behaviour from its native app, which owns REST there.
//
// Sending: the picker hands `invite(userIds)` the chosen people; this asks the worker for the facts
// (`callInviteContext`), decides with the pure `CallInvitePlan.decide`, and carries the plan out:
//   ring          → one DM each with the invite post;
//   addThenRing   → add the outsiders (a refusal drops only that person, named), then ring everyone left;
//   notAllowed    → nothing is sent, the line says who could not be added;
//   moveToGroup   → `/channels/group`, leave this call, join the group's (that starts it and rings
//                   the others through the ordinary D/G ring), then the move marker in the OLD
//                   conversation with the permalink of the group's call post;
//   tooMany       → «Una llamada de grupo admite hasta 8 personas».
// The call screen shows ONE line: «Invitando…» while it runs, then the outcome.
//
// Receiving: the worker hands over every `posted` marker (`callInvite` signal); a fresh invite to a
// live call rings through the existing incoming-call card (`CallInviteSignal.shouldRing`), a fresh
// move while this device is in the from-call follows it without asking (`shouldFollowMove`).
import { useSyncExternalStore } from 'react';
import { data } from '../../../bridge/client';
import type { ActiveCall } from '../../../core/activeCalls';
import { CallInvitePlan, CALL_INVITE_GROUP_LIMIT } from '../../../core/callInvitePlan';
import { CallInviteSignal } from '../../../core/callInviteSignal';
import type { CallInviteSignalPost } from '../../../data/features/callInvite';
import { t, tf } from '../../../i18n/i18n';
import { permalinkUrl } from '../../router';
import { callManager } from './callManager';

export interface CallInviteOutcome {
  text: string;
  tone: 'progress' | 'ok' | 'error';
}

export interface CallInviteUiState {
  pickerOpen: boolean;
  busy: boolean;
  outcome: CallInviteOutcome | null;
}

const OUTCOME_LIFETIME_MS = 8_000;
const CALL_POST_WAIT_MS = 12_000;

function log(line: string): void {
  console.info(`[callInvite] ${line}`);
  callManager.logCallEvent(`[callInvite] ${line}`);
}

function list(names: string[]): string {
  return names.filter(Boolean).join(', ');
}

/** Resolves once the active-call table shows a call in `channelId` that carries its post id. */
function waitForCall(channelId: string, timeoutMs: number): Promise<ActiveCall | null> {
  const find = () => callManager.getActive().calls.find((c) => c.channelId === channelId && c.postId) ?? null;
  const now = find();
  if (now) return Promise.resolve(now);
  return new Promise((resolve) => {
    const stop = callManager.subscribeActive(() => {
      const hit = find();
      if (!hit) return;
      clearTimeout(timer);
      stop();
      resolve(hit);
    });
    const timer = setTimeout(() => {
      stop();
      resolve(find());
    }, timeoutMs);
  });
}

class CallInviter {
  private state: CallInviteUiState = { pickerOpen: false, busy: false, outcome: null };
  private listeners = new Set<() => void>();
  private outcomeTimer: ReturnType<typeof setTimeout> | null = null;
  private wired = false;
  /** Markers already handled (a socket replay must not ring twice). */
  private seen = new Set<string>();

  getState = (): CallInviteUiState => this.state;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private set(patch: Partial<CallInviteUiState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private say(outcome: CallInviteOutcome | null): void {
    if (this.outcomeTimer) clearTimeout(this.outcomeTimer);
    this.outcomeTimer = null;
    this.set({ outcome });
    if (outcome && outcome.tone !== 'progress') this.outcomeTimer = setTimeout(() => this.set({ outcome: null }), OUTCOME_LIFETIME_MS);
  }

  openPicker(): void {
    if (callManager.getState().phase !== 'active') return;
    this.set({ pickerOpen: true });
  }

  closePicker(): void {
    if (this.state.pickerOpen) this.set({ pickerOpen: false });
  }

  dismissOutcome(): void {
    this.say(null);
  }

  /** The picker's «Invitar». `teamName` builds the permalinks (§25.4: the open team's slug). */
  async invite(userIds: string[], teamName: string): Promise<void> {
    const call = callManager.getState();
    const me = callManager.userId;
    if (call.phase !== 'active' || !me || !userIds.length || this.state.busy) return;
    const channelId = call.channelId;
    const title = call.channelName;
    this.set({ pickerOpen: false, busy: true });
    this.say({ text: t('web.call.invite.sending'), tone: 'progress' });
    try {
      const [context, names] = await Promise.all([data.call('callInviteContext', channelId, userIds), data.call('callInviteNames', userIds)]);
      if (!context) {
        this.say({ text: tf('web.call.invite.sendFailed', list(userIds.map((id) => names[id] ?? '?'))), tone: 'error' });
        return;
      }
      const nameOf = (ids: string[]) => list(ids.map((id) => names[id] ?? '?'));
      const invitable = userIds.filter((id) => !context.skipped.includes(id));
      if (!invitable.length) {
        this.say({ text: tf('web.call.invite.sendFailed', nameOf(userIds)), tone: 'error' });
        return;
      }
      const plan = CallInvitePlan.decide(context.channelType, context.memberIds, me, invitable, context.canAddMembers);
      log(`plan=${plan.kind} channel=${channelId} type=${context.channelType} invitees=${userIds.length}`);
      switch (plan.kind) {
        case 'tooMany':
          this.say({ text: tf('web.call.invite.tooMany', CALL_INVITE_GROUP_LIMIT), tone: 'error' });
          return;
        case 'notAllowed':
          this.say({ text: tf('web.call.invite.notAllowed', nameOf(plan.userIds)), tone: 'error' });
          return;
        case 'ring':
        case 'addThenRing': {
          let ring = plan.kind === 'ring' ? plan.invitees : plan.ring;
          const parts: CallInviteOutcome[] = [];
          if (plan.kind === 'addThenRing') {
            const added = await data.call('callInviteAdd', channelId, plan.toAdd);
            if (added.failed.length) {
              ring = ring.filter((id) => !added.failed.includes(id));
              parts.push({ text: tf('web.call.invite.addFailed', nameOf(added.failed)), tone: 'error' });
            }
          }
          if (ring.length) {
            const active = callManager.getActive().calls.find((c) => c.channelId === channelId);
            if (!active) {
              this.say({ text: tf('web.call.invite.sendFailed', nameOf(ring)), tone: 'error' });
              return;
            }
            const message = CallInviteSignal.inviteMessage(title, active.postId ? permalinkUrl(teamName, active.postId) : permalinkUrl(teamName, active.id));
            const result = await data.call('callInviteRing', ring, { channelId, callId: active.id, postId: active.postId }, message);
            if (result.rang.length) parts.unshift({ text: tf('web.call.invite.sent', nameOf(result.rang)), tone: 'ok' });
            if (result.failed.length) parts.push({ text: tf('web.call.invite.sendFailed', nameOf(result.failed)), tone: 'error' });
          }
          this.say({ text: parts.map((p) => p.text).join(' · '), tone: parts.some((p) => p.tone === 'error') ? 'error' : 'ok' });
          return;
        }
        case 'moveToGroup': {
          const group = await data.call('callInviteGroup', plan.ids);
          if (!group) {
            this.say({ text: t('web.call.invite.moveFailed'), tone: 'error' });
            return;
          }
          const groupTitle = list(group.names);
          callManager.moveTo(group.channelId, groupTitle);
          const groupCall = await waitForCall(group.channelId, CALL_POST_WAIT_MS);
          const callId = groupCall?.id ?? '';
          if (!callId) {
            log(`move: no call in ${group.channelId} after ${CALL_POST_WAIT_MS} ms`);
            this.say({ text: t('web.call.invite.moveFailed'), tone: 'error' });
            return;
          }
          const message = CallInviteSignal.movedMessage(group.names, permalinkUrl(teamName, groupCall?.postId || callId));
          await data.call('callInvitePostMoved', channelId, { fromChannelId: channelId, toChannelId: group.channelId, callId }, message);
          log(`moved from=${channelId} to=${group.channelId} call=${callId}`);
          this.say({ text: tf('web.call.invite.moved', groupTitle), tone: 'ok' });
          return;
        }
      }
    } catch (error) {
      log(`failed: ${String(error)}`);
      this.say({ text: t('web.call.invite.moveFailed'), tone: 'error' });
    } finally {
      this.set({ busy: false });
    }
  }

  /** Called once by CallInviteHost: the worker's marker posts. */
  wire(): void {
    if (this.wired) return;
    this.wired = true;
    data.onSignal((s) => {
      if (s.kind === 'callInvite') void this.onMarker(s.post);
    });
    // The picker belongs to the call: it goes when the call does.
    callManager.subscribe(() => {
      if (this.state.pickerOpen && callManager.getState().phase !== 'active') this.closePicker();
    });
  }

  private async onMarker(post: CallInviteSignalPost): Promise<void> {
    if (this.seen.has(post.id)) return;
    this.seen.add(post.id);
    const me = callManager.userId ?? '';
    const call = callManager.getState();
    const myCall = call.phase !== 'idle' ? call.channelId : null;
    const invite = CallInviteSignal.inviteFrom(post.props);
    if (invite) {
      const activeIn = () => callManager.getActive().calls.find((c) => c.channelId === invite.channelId) ?? null;
      // Just added to the channel (addThenRing): this device may not know its call yet — ask once.
      let active = activeIn();
      if (!active && post.userId !== me) {
        await data.call('refreshCalls').catch(() => undefined);
        active = activeIn();
      }
      const ring = CallInviteSignal.shouldRing(invite, post.createAt, Date.now(), active, myCall, post.userId, me);
      log(`invite post=${post.id} call=${invite.callId} ring=${ring}`);
      if (!ring) return;
      const title = CallInviteSignal.titleFromInviteMessage(post.message);
      callManager.ringInvite({ channelId: invite.channelId, callId: invite.callId, callerId: post.userId, callerName: post.authorName, inviteTitle: title || post.authorName });
      return;
    }
    const moved = CallInviteSignal.movedFrom(post.props);
    if (!moved) return;
    const follow = CallInviteSignal.shouldFollowMove(moved, post.createAt, Date.now(), myCall, post.userId, me);
    log(`moved post=${post.id} to=${moved.toChannelId} follow=${follow}`);
    if (!follow) return;
    // The group's name as THIS reader sees it: the marker names everyone but its author (me included).
    // A store read in the worker — the leave + join below waits for nothing else.
    const names = CallInviteSignal.namesFromMovedMessage(post.message);
    const mine = me ? (await data.call('callInviteNames', [me]).catch(() => ({}) as Record<string, string>))[me] : undefined;
    const groupTitle = list([post.authorName, ...(names ? names.split(', ') : [])].filter((n) => n && n !== mine));
    log(`follow from=${moved.fromChannelId} to=${moved.toChannelId}`);
    callManager.moveTo(moved.toChannelId, groupTitle);
    this.say({ text: tf('web.call.invite.moved', groupTitle), tone: 'ok' });
  }
}

export const callInviter = new CallInviter();

export function useCallInvite(): CallInviteUiState {
  return useSyncExternalStore(callInviter.subscribe, callInviter.getState);
}
